import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import useNotificationHandler from "hooks/useNotificationHandler";
import api from "service/api";
import { QUERY_KEYS } from "library/shared";

// Types based on the API collection structure
export interface CalculationRequestDto {
  CorrelationId?: string;
  SelectedProducts: string[];
  Mtpl?: {
    SelectedInsurers?: string[];
    SelectedMonthsOrPayments?: number[];
  };
  Casco?: {
    SelectedInsurers?: string[];
    SelectedMonthsOrPayments?: number[];
    IncludeAdditionalConditions?: boolean;
  };
  Common: {
    Language: string;
    CreatedTime: string;
    Guid: string;
    UserName: string;
    Public: string;
    Partner: string;
    Data?: boolean;
    OfferStatusId?: number | null;
    ObjectId?: string;
  };
  InsurerLogins?: {
    InsurerLogin: Array<{
      InsurerID: string;
      Login: string;
      Password: string;
    }>;
  };
  CustomParameters?: any[];
  Client: {
    IsExistingInGOS?: boolean;
    IsCompany: string;
    Code: string;
    Name: string;
    FirstName?: string;
    LastName?: string;
    DriverLicenceDate?: string;
    Experience?: string;
    BirthDate?: string;
    Age?: string;
    Region?: string | null;
    RegionCode?: string;
    Address: {
      AddressString?: string;
      Country?: string;
      City?: string;
      Suburb?: string | null;
      Village?: string | null;
      Street?: string;
      House?: string | null;
      Flat?: string | null;
      Index?: string;
      Phone?: string;
      Email?: string;
      MobilePhone?: string | null;
    };
    IsResident?: string;
    InvalidityCode?: string | null;
    Company?: boolean;
  };
  Vehicle: {
    LicensePlate?: string;
    Vin?: string;
    Brand: string;
    Model: string;
    RegistrationCertificateNumber?: string;
    PowerKw?: string;
    CubicCapacityCcm?: string;
    Seats?: string;
    Weight?: string;
    MaximumGrossWeight?: string;
    ManufactureDate?: string;
    FirstRegistrationDate?: string | null;
    OwnerCode?: string;
    TypeID?: string | null;
    TypeName?: string | null;
    Type?: string;
    Value: string;
    ValueAverage?: string | null;
    VehiclePowerTypeCode?: string | null;
    Fuel?: string;
    PurchaseValue?: string;
    EmpCode?: string | null;
    IsNew?: string;
    VehicleBodyTypeCode?: string | null;
    Owner?: {
      IsExistingInGOS?: boolean;
      IsCompany: string;
      Code: string;
      Name: string;
      FirstName?: string | null;
      LastName?: string | null;
      DriverLicenceDate?: string | null;
      Experience?: string | null;
      BirthDate?: string | null;
      Age?: string | null;
      Region?: string | null;
      RegionCode?: string;
      Address?: {
        AddressString?: string;
        Country?: string | null;
        City?: string | null;
        Suburb?: string | null;
        Village?: string | null;
        Street?: string | null;
        House?: string | null;
        Flat?: string | null;
        Index?: string | null;
        Phone?: string | null;
        Email?: string | null;
        MobilePhone?: string | null;
      };
      IsResident?: string;
      InvalidityCode?: string | null;
      Company?: boolean;
    };
    IsConvertible?: string;
    AllWheelDrive?: string;
    AutomaticTransmission?: string;
    FogLights?: string;
    LeatherInterior?: string;
    Conditioner?: string;
    OtherEquipment?: string | null;
    Advertisements?: string;
    AlloyWheels?: string;
    AlloyWheelsType?: string | null;
    AlloyWheelsDescription?: string | null;
    AlloyWheelsValue?: string | null;
    Tachograph?: string;
    AudioVideoEquipment?: string | null;
    AdditionalEquipment?: string | null;
    DetachableAudioFrontPanel?: string;
    Defects?: string | null;
    Category?: string | null;
    ClassID?: string | null;
    Class?: string | null;
    VehicleTransportTypeCode?: string | null;
    Tuning?: string;
    TuningDescription?: string | null;
    TuningValue?: string | null;
    AudioVideoEquipmentInstallation?: string;
    IsKeyless?: string;
    ChildSeat?: string;
    ChildSeatDesc?: string | null;
    ChildSeatValue?: string | null;
    RoofRackMounts?: string;
    RoofRackMountsDesc?: string | null;
    RoofRackMountsValue?: string | null;
    RoofBoxes?: string;
    RoofBoxesDesc?: string | null;
    RoofBoxesValue?: string | null;
    RoofHolders?: string;
    RoofHoldersDesc?: string | null;
    RoofHoldersValue?: string | null;
    Platforms?: string;
    PlatformsDesc?: string | null;
    PlatformsValue?: string | null;
    CustomLicensePlate?: string;
    CustomLicensePlateDesc?: string | null;
    CustomLicensePlateValue?: string | null;
    AudioVideoInstallation1?: string;
    AudioVideoInstallationDesc?: string | null;
    AudioVideoInstallationValue?: string | null;
    TowHitch?: string;
    TowHitchDesc?: string | null;
    TowHitchValue?: string | null;
    LeatherInteriorDesc?: string | null;
    LeatherInteriorValue?: string | null;
    GlassColouring?: string;
    GlassColouringDesc?: string | null;
    GlassColouringValue?: string | null;
    AdLabelsDesc?: string | null;
    AdLabelsValue?: string | null;
    GasEquipment?: string;
    GasEquipmentDesc?: string | null;
    GasEquipmentValue?: string | null;
    OtherEquipment1?: string;
    OtherEquipmentDesc?: string | null;
    OtherEquipmentValue?: string;
    ManufactureYear?: number;
  };
  Insurance: {
    Renewal?: string;
    StartDate: string;
    EndDate?: string | null;
    ObjectUsedAbroad?: string | null;
    ObjectUsageAbroad?: string;
    DriverBirthDate?: string | null;
    DriverAge?: string | null;
    DriverLicenceDate?: string | null;
    DriverExperience?: string | null;
    Currency: string;
    CoverageTerritory?: string;
    ObjectUsage?: number | null;
    ObjectLeased?: string;
    LeasingStartDate?: string | null;
    Duration?: string | null;
    Intermediary?: string | null;
    Payments?: string | null;
    GreenCardRequired?: string | null;
    Deductible?: string | null;
    TheftDeductible?: string | null;
    CourtesyCar?: string | null;
    PassengerInsurance?: string | null;
    RoadAssitance?: string | null;
    NewValue?: string | null;
    FirstPaymentDate?: string;
    DiscountInPercents?: string | null;
  };
  VehicleSecurity?: {
    Alarm?: string;
    AlarmKeyCount?: string | null;
    Alarm1?: string;
    AlarmKeyCount1?: string | null;
    Alarm2?: string;
    AlarmKeyCount2?: string | null;
    Immobiliser?: string;
    ImmobiliserKeyCount?: string;
    Immobiliser1?: string;
    ImmobiliserKeyCount1?: string | null;
    Immobiliser2?: string;
    ImmobiliserKeyCount2?: string | null;
    Immobiliser3?: string;
    ImmobiliserKeyCount3?: string | null;
    ContactlessImmobiliser?: string;
    SecretSwitch?: string;
    TrackingSystem?: string;
    OtherSecuritySystems?: string;
    KeyCount?: string | null;
    FactoryImmobiliserInKey?: string;
    OtherSecuritySystemsKeyCount?: string | null;
  };
  YoungestDriverAge?: number;
  SmallestDriverExperience?: number;
  VehicleEquipmentSum?: number;
  HasVehicleEquipment?: boolean;
  Country?: number;
  Requester?: string | null;
  PreviousInsuranceEndDate?: string | null;
  CurrentInsuranceStartDate?: string | null;
}

export interface CalculationResponseDto {
  CalculationId: string;
  CalculationRequestedAt: string;
  CalculationRequestedBy: string;
  CalculationRequestedByUsername: string;
  CalculationRequestedByUserFullName: string;
  Calculation: {
    Request: any;
    Response: {
      Mtpl: any[];
      Casco: any[];
    };
  };
  IsInvalidated: boolean;
  InvalidatedAt: string | null;
  InvalidatedBy: string | null;
  InvalidatedByUsername: string | null;
  InvalidatedByUserFullName: string | null;
  IsArchived: boolean;
  SecurityInformation: string[];
  OfferedInsurersCasco: string[];
  OfferedInsurersMtpl: string[];
  _etag: string | null;
}

const calculateInsuranceQuote = async (
  data: CalculationRequestDto
): Promise<CalculationResponseDto> => {
  // Use the actual data parameter passed in, not the hardcoded example
  console.log("=== Sending Calculate API Request ===");
  console.log("Request data:", data);
  console.log("Mtpl.SelectedInsurers:", data.Mtpl?.SelectedInsurers);
  console.log("Casco.SelectedInsurers:", data.Casco?.SelectedInsurers);
  console.log("SelectedProducts:", data.SelectedProducts);

  const response = await api.post(`/calculations`, data);
  return response.data;
};

const getCalculationData = async (
  calculationId: string
): Promise<CalculationResponseDto> => {
  const response = await api.get(`/calculations/${calculationId}`);
  return response.data;
};

export function useCalculationMutation(
  onSuccessCallback?: (data: CalculationResponseDto) => void
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<CalculationResponseDto, Error, CalculationRequestDto>({
    mutationFn: calculateInsuranceQuote,
    onSuccess: (calculationData) => {
      toast.success(t("qtg.calculation.success"));
      if (onSuccessCallback) {
        onSuccessCallback(calculationData);
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

export function useCalculationQuery(
  calculationId: string,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: CalculationResponseDto) => void;
    onError?: (error: Error) => void;
  }
) {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  const result = useQuery<CalculationResponseDto, Error>({
    queryKey: [QUERY_KEYS.calculations, calculationId],
    queryFn: () => getCalculationData(calculationId),
    enabled: options?.enabled ?? !!calculationId,
  });

  useEffect(() => {
    if (result.isSuccess && options?.onSuccess) {
      options.onSuccess(result.data);
    }
    if (result.error) {
      handleAddAxiosErrorNotification(result.error);
      options?.onError?.(result.error);
    }
  }, [
    result.isSuccess,
    result.error,
    result.data,
    options,
    handleAddAxiosErrorNotification,
  ]);

  return result;
}
